import { ValueObject } from '../../../shared/models/ValueObject';

/**
 * 인증 토큰 값 객체
 */
export class AuthToken extends ValueObject<{
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
}> {
  private readonly _accessToken: string;
  private readonly _refreshToken: string;
  private readonly _expiresAt: Date;
  private readonly _issuedAt: Date;

  constructor(props: {
    accessToken: string;
    refreshToken: string;
    expiresAt: Date;
    issuedAt?: Date;
  }) {
    super();
    this.validate(props);
    this._accessToken = props.accessToken;
    this._refreshToken = props.refreshToken;
    this._expiresAt = new Date(props.expiresAt);
    this._issuedAt = props.issuedAt || new Date();
  }

  get accessToken(): string {
    return this._accessToken;
  }

  get refreshToken(): string {
    return this._refreshToken;
  }

  get expiresAt(): Date {
    return new Date(this._expiresAt);
  }

  get issuedAt(): Date {
    return new Date(this._issuedAt);
  }

  private validate(props: {
    accessToken: string;
    refreshToken: string;
    expiresAt: Date;
  }): void {
    if (!props.accessToken) {
      throw new Error('액세스 토큰은 비어있을 수 없습니다');
    }

    if (!props.refreshToken) {
      throw new Error('리프레시 토큰은 비어있을 수 없습니다');
    }

    if (!props.expiresAt || !(props.expiresAt instanceof Date)) {
      throw new Error('유효한 만료 시간이 필요합니다');
    }

    if (props.expiresAt <= new Date()) {
      throw new Error('토큰 만료 시간은 현재 시간보다 이후여야 합니다');
    }
  }

  /**
   * 토큰 만료 여부 확인
   */
  isExpired(): boolean {
    return new Date() >= this._expiresAt;
  }

  /**
   * 토큰 만료까지 남은 시간 (밀리초)
   */
  getTimeToExpiry(): number {
    return Math.max(0, this._expiresAt.getTime() - new Date().getTime());
  }

  /**
   * 토큰 만료까지 남은 시간 (분)
   */
  getMinutesToExpiry(): number {
    return Math.floor(this.getTimeToExpiry() / (1000 * 60));
  }

  /**
   * 토큰 갱신이 필요한지 확인 (만료 5분 전)
   */
  needsRefresh(): boolean {
    return this.getMinutesToExpiry() <= 5;
  }

  /**
   * 토큰의 유효성 확인
   */
  isValid(): boolean {
    return !this.isExpired() && this._accessToken.length > 0;
  }

  /**
   * JWT 토큰에서 페이로드 추출 (Base64 디코딩)
   */
  getPayload(): any {
    try {
      const parts = this._accessToken.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const payload = parts[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  }

  /**
   * 토큰 타입 확인
   */
  getTokenType(): string {
    const payload = this.getPayload();
    return payload?.typ || 'JWT';
  }

  /**
   * 토큰 발급자 확인
   */
  getIssuer(): string | null {
    const payload = this.getPayload();
    return payload?.iss || null;
  }

  /**
   * 마스킹된 토큰 표시 (보안용)
   */
  getMaskedToken(): string {
    if (this._accessToken.length <= 10) {
      return '***';
    }
    return `${this._accessToken.substring(0, 6)}...${this._accessToken.substring(this._accessToken.length - 4)}`;
  }

  protected equalsCore(vo: AuthToken): boolean {
    return (
      this._accessToken === vo._accessToken &&
      this._refreshToken === vo._refreshToken &&
      this._expiresAt.getTime() === vo._expiresAt.getTime()
    );
  }

  protected copy(): AuthToken {
    return new AuthToken({
      accessToken: this._accessToken,
      refreshToken: this._refreshToken,
      expiresAt: this._expiresAt,
      issuedAt: this._issuedAt,
    });
  }

  toString(): string {
    return this.getMaskedToken();
  }

  /**
   * JSON 변환 (보안 정보 제외)
   */
  toJSON(): object {
    return {
      maskedToken: this.getMaskedToken(),
      expiresAt: this._expiresAt.toISOString(),
      issuedAt: this._issuedAt.toISOString(),
      isExpired: this.isExpired(),
      needsRefresh: this.needsRefresh(),
      minutesToExpiry: this.getMinutesToExpiry(),
    };
  }

  /**
   * 팩토리 메서드
   */
  static create(props: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number; // 초 단위
  }): AuthToken {
    const expiresAt = new Date(Date.now() + props.expiresIn * 1000);
    return new AuthToken({
      accessToken: props.accessToken,
      refreshToken: props.refreshToken,
      expiresAt,
    });
  }

  /**
   * JWT 토큰으로부터 생성
   */
  static fromJWT(accessToken: string, refreshToken: string): AuthToken {
    // JWT에서 exp 클레임 추출
    try {
      const parts = accessToken.split('.');
      const payload = JSON.parse(atob(parts[1]));
      const expiresAt = new Date(payload.exp * 1000);
      
      return new AuthToken({
        accessToken,
        refreshToken,
        expiresAt,
      });
    } catch (error) {
      throw new Error('유효하지 않은 JWT 토큰입니다');
    }
  }
}